/**
 * Brazilian Validation Utilities
 *
 * Validators specific to Brazilian documents and formats:
 * - CPF (Cadastro de Pessoas Físicas) with checksum validation
 * - CNPJ (Cadastro Nacional da Pessoa Jurídica) with checksum validation
 * - Phone numbers (10-11 digits)
 * - CEP (Código de Endereçamento Postal)
 *
 * @module validation/brazilian-validators
 */

import * as z from 'zod';
import { VALIDATION_MESSAGES } from './messages';

/**
 * CPF format regex: XXX.XXX.XXX-XX or XXXXXXXXXXX
 */
const CPF_REGEX = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/;

/**
 * CNPJ format regex: XX.XXX.XXX/XXXX-XX or XXXXXXXXXXXXXX
 */
const CNPJ_REGEX = /^(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})$/;

/**
 * Phone format regex: (XX) XXXXX-XXXX or (XX) XXXX-XXXX or XXXXXXXXXXX or XXXXXXXXXX
 */
const PHONE_REGEX = /^(\(?\d{2}\)?\s?)?\d{4,5}-?\d{4}$/;

/**
 * CEP format regex: XXXXX-XXX or XXXXXXXX
 */
const CEP_REGEX = /^\d{5}-?\d{3}$/;

/**
 * Validates CPF checksum digits
 * @param cpf - CPF string (with or without formatting)
 * @returns true if CPF is valid, false otherwise
 */
export function validateCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return false;

  // Check for known invalid CPFs (all same digit)
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  // Validate first checksum digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;

  // Validate second checksum digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;

  return true;
}

/**
 * Validates CNPJ checksum digits
 * @param cnpj - CNPJ string (with or without formatting)
 * @returns true if CNPJ is valid, false otherwise
 */
export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return false;

  // Check for known invalid CNPJs (all same digit)
  if (/^(\d)\1{13}$/.test(cleaned)) return false;

  // Validate first checksum digit
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights1[i];
  }
  let digit = sum % 11;
  digit = digit < 2 ? 0 : 11 - digit;
  if (digit !== parseInt(cleaned.charAt(12))) return false;

  // Validate second checksum digit
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights2[i];
  }
  digit = sum % 11;
  digit = digit < 2 ? 0 : 11 - digit;
  if (digit !== parseInt(cleaned.charAt(13))) return false;

  return true;
}

/**
 * CPF validator with format check and checksum validation
 */
export const cpfValidator = z
  .string()
  .min(1, { message: VALIDATION_MESSAGES.document.required })
  .regex(CPF_REGEX, { message: VALIDATION_MESSAGES.document.formatInvalid })
  .refine(validateCPF, { message: VALIDATION_MESSAGES.document.cpfInvalid });

/**
 * Optional CPF validator
 */
export const optionalCPFValidator = z
  .string()
  .regex(CPF_REGEX, { message: VALIDATION_MESSAGES.document.formatInvalid })
  .refine(
    (val) => !val || validateCPF(val),
    { message: VALIDATION_MESSAGES.document.cpfInvalid }
  )
  .optional()
  .or(z.literal(''));

/**
 * CNPJ validator with format check and checksum validation
 */
export const cnpjValidator = z
  .string()
  .min(1, { message: VALIDATION_MESSAGES.document.required })
  .regex(CNPJ_REGEX, { message: VALIDATION_MESSAGES.document.formatInvalid })
  .refine(validateCNPJ, { message: VALIDATION_MESSAGES.document.cnpjInvalid });

/**
 * Optional CNPJ validator
 */
export const optionalCNPJValidator = z
  .string()
  .regex(CNPJ_REGEX, { message: VALIDATION_MESSAGES.document.formatInvalid })
  .refine(
    (val) => !val || validateCNPJ(val),
    { message: VALIDATION_MESSAGES.document.cnpjInvalid }
  )
  .optional()
  .or(z.literal(''));

/**
 * Brazilian phone number validator (10-11 digits with optional formatting)
 */
export const phoneValidator = z
  .string()
  .min(1, { message: VALIDATION_MESSAGES.phone.required })
  .regex(PHONE_REGEX, { message: VALIDATION_MESSAGES.phone.invalid })
  .refine(
    (val) => {
      const digits = val.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 11;
    },
    { message: VALIDATION_MESSAGES.phone.invalid }
  );

/**
 * Optional phone validator
 */
export const optionalPhoneValidator = z
  .string()
  .regex(PHONE_REGEX, { message: VALIDATION_MESSAGES.phone.invalid })
  .refine(
    (val) => {
      if (!val) return true;
      const digits = val.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 11;
    },
    { message: VALIDATION_MESSAGES.phone.invalid }
  )
  .optional()
  .or(z.literal(''));

/**
 * CEP validator (Brazilian postal code)
 */
export const cepValidator = z
  .string()
  .min(1, { message: VALIDATION_MESSAGES.cep.required })
  .regex(CEP_REGEX, { message: VALIDATION_MESSAGES.cep.invalid });

/**
 * Optional CEP validator
 */
export const optionalCEPValidator = z
  .string()
  .regex(CEP_REGEX, { message: VALIDATION_MESSAGES.cep.invalid })
  .optional()
  .or(z.literal(''));

/**
 * Format CPF for display
 * @param cpf - CPF string (digits only)
 * @returns Formatted CPF (XXX.XXX.XXX-XX)
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Format CNPJ for display
 * @param cnpj - CNPJ string (digits only)
 * @returns Formatted CNPJ (XX.XXX.XXX/XXXX-XX)
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Format phone for display
 * @param phone - Phone string (digits only)
 * @returns Formatted phone ((XX) XXXXX-XXXX or (XX) XXXX-XXXX)
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Format CEP for display
 * @param cep - CEP string (digits only)
 * @returns Formatted CEP (XXXXX-XXX)
 */
export function formatCEP(cep: string): string {
  const cleaned = cep.replace(/\D/g, '');
  if (cleaned.length !== 8) return cep;
  return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
}
